import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import type {
  SupplierResponse,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  ListSuppliersResponse,
  ListSuppliersRequest,
  CreateSupplierResponse,
  UpdateSupplierResponse,
} from '../models';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root'
})
export class SupplierService {
  private http = inject(HttpClient);

  private readonly API_BASE_URL = environment.RETAIL_BILLING_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  private buildParams(params: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined) {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return httpParams;
  }

  getCompanySuppliers(request?: ListSuppliersRequest): Observable<ListSuppliersResponse> {
    const params: Record<string, unknown> = {
      PageNumber: request?.page || 1,
      PageSize: request?.page_size || 10
    };

    if (request?.search_term) {
      params['SearchTerm'] = request.search_term;
    }
    if (request?.is_active !== undefined) {
      params['IsActive'] = request.is_active;
    }

    return this.http.get<ListSuppliersResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.GET_SUPPLIERS,
      {
        headers: this.httpOptions.headers,
        params: this.buildParams(params)
      }
    ).pipe(
      map((res) => res),
      catchError((error) => throwError(() => new AppErrors(error)))
    );
  }

  getCompanySupplier(supplierId: number): Observable<SupplierResponse> {
    return this.http.get<SupplierResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.GET_SUPPLIER_BY_ID
        .replace('{supplier_id}', supplierId.toString()),
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => throwError(() => new AppErrors(error)))
    );
  }

  createCompanySupplier(request: CreateSupplierRequest): Observable<CreateSupplierResponse> {
    return this.http.post<CreateSupplierResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.CREATE_SUPPLIER,
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => throwError(() => new AppErrors(error)))
    );
  }

  updateCompanySupplier(supplierId: number, request: UpdateSupplierRequest): Observable<UpdateSupplierResponse> {
    return this.http.put<UpdateSupplierResponse>(
      this.API_BASE_URL + CONST_URL_SEGEMENTS.RETAIL.UPDATE_SUPPLIER
        .replace('{supplier_id}', supplierId.toString()),
      request,
      {
        headers: this.httpOptions.headers
      }
    ).pipe(
      map((res) => res),
      catchError((error) => throwError(() => new AppErrors(error)))
    );
  }
}
